/**
 * 💰 수납 내역 (운영자)
 *
 * ## 왜 청구서 화면과 따로 두나
 * 「구독·청구」는 **청구서 단위**로 본다 — 이 청구서가 완납인가, 미납액이
 * 얼마인가. 그런데 운영자가 실제로 답해야 하는 질문은 그 아래에 있다.
 *
 *   · 이번 달에 **얼마가 들어왔나** (카드 대 계좌이체)
 *   · 자동결제가 **왜 실패했나** — 한도초과인지 유효기간 만료인지
 *   · 그 실패가 **몇 번째 시도인지**
 *   · 통장에 찍힌 **입금자명**이 누구인지 (상호와 다를 수 있다)
 *
 * 이 값들은 전부 `billing.payment` 에 쌓이고 있었는데 꺼내 보는 화면이
 * 없었다. 결제가 실패하면 청구서에 「미납」이라고만 뜨고, 고객에게 무엇을
 * 고치라고 말해야 하는지는 DB 를 열어야 알 수 있었다.
 *
 * ## 합계는 서버가 센다
 * 화면이 받은 행만 더하면 목록 상한(limit)에 걸린 뒤부터 조용히 틀린
 * 숫자가 된다. 같은 조건으로 서버가 세어 내려 준 값을 그대로 쓴다.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface PaymentRow {
  payment_id: string;
  tenant_id: string;
  invoice_id: string | null;
  invoice_no: string | null;
  company_name: string;
  provider: string;
  status: string;
  method: string;
  payer_name: string | null;
  amount: number;
  refunded_amount: number;
  attempt_no: number | null;
  requested_at: string;
  approved_at: string | null;
  failed_at: string | null;
  failure_code: string | null;
  failure_message: string | null;
  receipt_url: string | null;
  provider_payment_id: string | null;
  merchant_uid: string | null;
  memo: string | null;
}

interface PaymentList {
  items: PaymentRow[];
  summary: {
    paid_total: number;
    card_count: number;
    transfer_count: number;
    failed_count: number;
    refunded_total: number;
  };
}

/**
 * billing.payment 의 ck_payment_status 와 **같은 목록**이어야 한다.
 * 빠진 값은 화면에 영문 그대로 찍힌다 — PG 를 켜면 CANCELED 가 생긴다.
 */
const STATUS: Record<string, { text: string; color: string }> = {
  PENDING: { text: '대기', color: 'default' },
  AUTHORIZED: { text: '승인대기', color: 'blue' },
  PAID: { text: '성공', color: 'green' },
  FAILED: { text: '실패', color: 'red' },
  CANCELED: { text: '취소', color: 'default' },
  REFUNDED: { text: '환불', color: 'orange' },
  PARTIAL_REFUNDED: { text: '부분환불', color: 'orange' },
};

const METHOD: Record<string, string> = {
  CARD: '카드',
  TRANSFER: '계좌이체',
  MANUAL: '기타',
};

const won = (v: number | undefined | null) =>
  `${Math.round(v ?? 0).toLocaleString('ko-KR')}원`;

export default function PaymentPage() {
  const { message, modal } = AntdApp.useApp();
  const client = useQueryClient();
  const navigate = useNavigate();

  /*
   * 기본 기간은 이번 달이다. 운영자가 이 화면을 여는 가장 흔한 이유가
   * "이번 달 얼마 들어왔나" 라서다. 전체를 먼저 보여 주면 지난해 자료까지
   * 끌어와 느리기만 하고 답도 아니다.
   */
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [method, setMethod] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');
  const [refundTarget, setRefundTarget] = useState<PaymentRow | null>(null);
  const [refundForm] = Form.useForm();

  const params = useMemo(
    () => ({
      from: range?.[0]?.format('YYYY-MM-DD'),
      to: range?.[1]?.format('YYYY-MM-DD'),
      method,
      status,
      keyword: keyword.trim() || undefined,
      limit: 500,
    }),
    [range, method, status, keyword],
  );

  const list = useQuery<PaymentList, NormalizedError>({
    queryKey: ['admin', 'payments', params],
    queryFn: async () =>
      (await apiClient.get<PaymentList>('/admin/billing/payments', { params })).data,
  });

  const refund = useMutation({
    mutationFn: async (values: { amount: number; reason: string }) => {
      if (!refundTarget) return;
      await apiClient.post(`/admin/billing/payments/${refundTarget.payment_id}/refund`, {
        tenantId: refundTarget.tenant_id,
        amount: values.amount,
        reason: values.reason,
      });
    },
    onSuccess: async () => {
      message.success('환불을 처리했습니다.');
      setRefundTarget(null);
      refundForm.resetFields();
      await client.invalidateQueries({ queryKey: ['admin', 'payments'] });
      // 청구서·매출 숫자도 함께 달라진다.
      await client.invalidateQueries({ queryKey: ['admin', 'billing'] });
      await client.invalidateQueries({ queryKey: ['admin', 'revenue'] });
    },
    onError: (err) => message.error(errorText(err as unknown as NormalizedError)),
  });

  const openRefund = (row: PaymentRow) => {
    setRefundTarget(row);
    refundForm.setFieldsValue({
      // 남은 금액을 기본값으로 둔다. 부분 환불이 이미 있었다면 그만큼 뺀다.
      amount: Math.max(row.amount - (row.refunded_amount ?? 0), 0),
      reason: '',
    });
  };

  const summary = list.data?.summary;

  const columns: ColumnsType<PaymentRow> = [
    {
      title: '입금일시',
      key: 'when',
      width: 150,
      render: (_, r) => {
        /*
         * 실패 건에는 approved_at 이 없다. 시도한 시각을 보여 주고
         * 「시도」라고 적어 둔다 — 빈 칸으로 두면 고장으로 보인다.
         */
        const at = r.approved_at ?? r.failed_at ?? r.requested_at;
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{dayjs(at).format('MM-DD HH:mm')}</Text>
            {!r.approved_at && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                시도
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '기업',
      dataIndex: 'company_name',
      width: 170,
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          {/* 입금자명이 상호와 다를 때만 보여 준다. 같으면 줄만 길어진다. */}
          {r.payer_name && r.payer_name !== v && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              입금자 {r.payer_name}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '청구서',
      dataIndex: 'invoice_no',
      width: 130,
      render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: '수단',
      dataIndex: 'method',
      width: 86,
      render: (v: string) => METHOD[v] ?? v,
    },
    {
      title: '금액',
      dataIndex: 'amount',
      width: 120,
      align: 'right',
      render: (v: number, r) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Text strong>{won(v)}</Text>
          {r.refunded_amount > 0 && (
            <Text type="warning" style={{ fontSize: 11 }}>
              환불 {won(r.refunded_amount)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 92,
      render: (v: string, r) => {
        const meta = STATUS[v] ?? { text: v, color: 'default' };
        return (
          <Space size={4}>
            <Tag color={meta.color}>{meta.text}</Tag>
            {/* 재시도 회차는 2회차부터 의미가 있다 */}
            {(r.attempt_no ?? 1) > 1 && <Tag>{r.attempt_no}회차</Tag>}
          </Space>
        );
      },
    },
    {
      title: '실패 사유 / 메모',
      key: 'reason',
      render: (_, r) =>
        r.status === 'FAILED' ? (
          /*
           * 여기가 이 화면을 만든 이유다. 고객에게 전화해서 "카드 한도를
           * 확인해 주세요" 라고 말하려면 이 값이 보여야 한다.
           */
          <Space direction="vertical" size={0}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {r.failure_message ?? '사유 없음'}
            </Text>
            {r.failure_code && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                코드 {r.failure_code}
              </Text>
            )}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.memo ?? ''}
          </Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          {r.receipt_url && (
            <Tooltip title="매출전표 열기">
              <Button
                size="small"
                type="link"
                icon={<LinkOutlined />}
                href={r.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Tooltip>
          )}
          {/* 환불은 성공한 결제에만, 남은 금액이 있을 때만 */}
          {['PAID', 'PARTIAL_REFUNDED'].includes(r.status) &&
            r.amount - (r.refunded_amount ?? 0) > 0 && (
              <Button size="small" type="link" danger onClick={() => openRefund(r)}>
                환불
              </Button>
            )}
          <Button
            size="small"
            type="link"
            onClick={() =>
              modal.info({
                title: '수납 상세',
                width: 560,
                content: (
                  <Descriptions column={1} size="small" bordered style={{ marginTop: 12 }}>
                    <Descriptions.Item label="기업">{r.company_name}</Descriptions.Item>
                    <Descriptions.Item label="입금자명">{r.payer_name ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="청구서">{r.invoice_no ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="수단">{METHOD[r.method] ?? r.method}</Descriptions.Item>
                    <Descriptions.Item label="금액">{won(r.amount)}</Descriptions.Item>
                    <Descriptions.Item label="환불액">{won(r.refunded_amount)}</Descriptions.Item>
                    <Descriptions.Item label="요청">
                      {dayjs(r.requested_at).format('YYYY-MM-DD HH:mm:ss')}
                    </Descriptions.Item>
                    <Descriptions.Item label="승인">
                      {r.approved_at ? dayjs(r.approved_at).format('YYYY-MM-DD HH:mm:ss') : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="대행사">{r.provider}</Descriptions.Item>
                    {/* PG 에 문의할 때 이 두 값을 불러 준다 */}
                    <Descriptions.Item label="PG 거래번호">
                      {r.provider_payment_id ?? '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="주문번호">{r.merchant_uid ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="메모">{r.memo ?? '—'}</Descriptions.Item>
                  </Descriptions>
                ),
              })
            }
          >
            상세
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space wrap size="middle">
          <RangePicker
            value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
            allowClear
          />
          <Select
            placeholder="수단 전체"
            allowClear
            style={{ width: 130 }}
            value={method}
            onChange={setMethod}
            options={[
              { value: 'CARD', label: '카드' },
              { value: 'TRANSFER', label: '계좌이체' },
              { value: 'MANUAL', label: '기타' },
            ]}
          />
          <Select
            placeholder="상태 전체"
            allowClear
            style={{ width: 130 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'PAID', label: '성공' },
              { value: 'FAILED', label: '실패' },
              { value: 'PARTIAL_REFUNDED', label: '부분환불' },
              { value: 'REFUNDED', label: '환불' },
              { value: 'CANCELED', label: '취소' },
              { value: 'PENDING', label: '대기' },
              { value: 'AUTHORIZED', label: '승인대기' },
            ]}
          />
          <Input.Search
            placeholder="기업명 · 입금자명 · 청구서번호"
            allowClear
            style={{ width: 260 }}
            onSearch={setKeyword}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void list.refetch()} />
        </Space>
      </Card>

      {list.isError && <Alert type="error" showIcon message={errorText(list.error)} />}

      {/*
        이 화면은 **들어온 돈을 보는 곳**이고, 넣는 곳이 아니다. 입금을
        기록하려면 어느 청구서에 넣을지를 골라야 해서 청구서 쪽에 단추가
        있다. 그 사정을 모르면 여기서 「등록」 단추를 찾게 된다 — 실제로
        그랬다. 가는 길을 적어 둔다.
      */}
      <Alert
        type="info"
        showIcon
        message="입금을 기록하려면 「구독·청구」에서"
        description={
          <>
            이 화면은 <b>들어온 돈을 보는 곳</b>입니다. 계좌이체 입금을 새로 적으려면{' '}
            <b>과금 → 구독·청구 → 청구서 목록</b> 에서 그 청구서의{' '}
            <Tag>수납 기록</Tag> 단추를 누르세요. 어느 청구서에 넣을지를 골라야 하므로
            청구서 쪽에 있습니다. 적고 나면 그 내역이 여기 목록에 나타납니다.
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              여기서 할 수 있는 일은 <b>환불</b>입니다. 이미 받은 돈을 돌려줄 때 그 줄의
              「환불」을 누릅니다.
            </Text>
          </>
        }
        action={
          <Button size="small" onClick={() => navigate('/app/admin-billing')}>
            구독·청구 열기
          </Button>
        }
      />

      <Card size="small">
        <Space size="large" wrap>
          <Statistic
            title={
              <Tooltip title="이 조건으로 걸린 결제의 합계입니다. 환불은 빼지 않은 총액이라, 옆의 「환불」을 다시 빼지 마세요 — 순액은 맨 오른쪽에 있습니다.">
                결제 합계
              </Tooltip>
            }
            value={summary?.paid_total ?? 0}
            precision={0}
            suffix="원"
          />
          <Statistic title="카드" value={summary?.card_count ?? 0} suffix="건" />
          <Statistic title="계좌이체" value={summary?.transfer_count ?? 0} suffix="건" />
          <Statistic
            title="실패"
            value={summary?.failed_count ?? 0}
            suffix="건"
            valueStyle={{ color: (summary?.failed_count ?? 0) > 0 ? '#cf1322' : undefined }}
          />
          <Statistic
            title={
              <Tooltip title="이 결제들에 대해 지금까지 돌려준 금액의 누계입니다. 환불한 날짜와 무관합니다.">
                환불 누계
              </Tooltip>
            }
            value={summary?.refunded_total ?? 0}
            precision={0}
            suffix="원"
          />
          <Statistic
            title={
              <Tooltip title="결제 합계에서 환불 누계를 뺀 값입니다. 매출 현황의 「수납」은 환불을 돌려준 달에서 빼므로, 달을 넘겨 환불하면 두 숫자가 다를 수 있습니다.">
                순액
              </Tooltip>
            }
            value={(summary?.paid_total ?? 0) - (summary?.refunded_total ?? 0)}
            precision={0}
            suffix="원"
            valueStyle={{ color: '#3f8600' }}
          />
        </Space>
      </Card>

      {/*
        실패가 있으면 눈에 띄게 알린다. 목록을 훑어야 알 수 있으면
        며칠 지나도록 모르고 지나간다 — 그 사이 서비스가 정지된다.
      */}
      {(summary?.failed_count ?? 0) > 0 && status !== 'FAILED' && (
        <Alert
          type="warning"
          showIcon
          message={`이 기간에 실패한 결제가 ${summary?.failed_count}건 있습니다`}
          description="사유를 확인하고 고객에게 안내해 주세요. 재시도는 자동으로 이뤄집니다."
          action={
            <Button size="small" onClick={() => setStatus('FAILED')}>
              실패만 보기
            </Button>
          }
        />
      )}

      <Table<PaymentRow>
        rowKey="payment_id"
        size="small"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        columns={columns}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        locale={{ emptyText: '이 조건에 해당하는 수납 내역이 없습니다.' }}
      />

      <Modal
        open={Boolean(refundTarget)}
        title="환불"
        onCancel={() => setRefundTarget(null)}
        onOk={async () => {
          const values = await refundForm.validateFields().catch(() => null);
          if (values) refund.mutate(values);
        }}
        confirmLoading={refund.isPending}
        okText="환불"
        okButtonProps={{ danger: true }}
        cancelText="취소"
        destroyOnClose
      >
        <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="기업">{refundTarget?.company_name}</Descriptions.Item>
          <Descriptions.Item label="결제액">{won(refundTarget?.amount)}</Descriptions.Item>
          <Descriptions.Item label="기환불">
            {won(refundTarget?.refunded_amount)}
          </Descriptions.Item>
        </Descriptions>
        <Form form={refundForm} layout="vertical">
          <Form.Item
            name="amount"
            label="환불 금액"
            rules={[{ required: true, message: '환불 금액을 입력하세요' }]}
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={1}
              max={Math.max((refundTarget?.amount ?? 0) - (refundTarget?.refunded_amount ?? 0), 1)}
              precision={0}
              formatter={(v) => `${Number(v ?? 0).toLocaleString('ko-KR')}`}
              parser={(v) => Number((v ?? '').replace(/[^\d]/g, ''))}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="사유"
            rules={[{ required: true, message: '사유를 입력하세요' }]}
            extra="감사 기록에 남습니다. 나중에 왜 환불했는지 설명할 수 있어야 합니다."
          >
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
